<template>
  <div class="home-dashboard">
    <!-- 第一段:实时指标 + 下载 -->
    <el-row
      :gutter="16"
      class="dashboard-head"
    >
      <el-col
        v-if="hasPermission(['sys:view:dashboard:analytics'])"
        :xs="24"
        :sm="24"
        :md="18"
        :lg="19"
      >
        <el-card
          class="box-card"
          shadow="never"
        >
          <template #header>
            <span class="card-title">Real Time</span>
          </template>
          <statisticsRealtime
            v-if="stats"
            :stats="stats"
          />
        </el-card>
      </el-col>
      <el-col
        :xs="24"
        :sm="24"
        :md="6"
        :lg="5"
      >
        <download />
      </el-col>
    </el-row>

    <!-- 第二段:房间趋势图 + 明细表 -->
    <el-row
      :gutter="16"
      class="dashboard-main"
    >
      <el-col
        :xs="24"
        :sm="24"
        :md="16"
        :lg="17"
      >
        <el-card
          class="box-card"
          shadow="never"
        >
          <template #header>
            <span class="card-title">Room Daily Trend</span>
          </template>
          <div
            v-if="hasRoomData"
            class="room-chart"
          >
            <Chart :options="roomOptions" />
          </div>
          <div
            v-else
            class="room-chart room-empty"
          >
            No data
          </div>
        </el-card>
      </el-col>
      <el-col
        :xs="24"
        :sm="24"
        :md="8"
        :lg="7"
      >
        <el-card
          class="box-card"
          shadow="never"
        >
          <template #header>
            <span class="card-title">Room Detail</span>
          </template>
          <statisticsRoom :room="room" />
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import statisticsRealtime from '../../views/home/statistics-realtime'
import statisticsRoom from '../../views/home/statistics-room'
import download from '../../views/home/download'
import Chart from '@/components/charts/Charts'
import * as echarts from 'echarts'
import 'echarts/lib/component/tooltip'
import { getRealTimeStats, getRoomStats } from '../../api/statistics'
import { convertDate, hasPermission } from '../../utils/util'
import moment from 'moment'

const stats = ref({
  inQueueVisitors: 0,
  chattingVisitors: 0,
  totalVisitors: 0,
  loggedInAgent: 0,
  chattingAgent: 0,
  robotChattingVisitors: 0,
})

let timer = null

const room = ref([
  {
    recordTime: null,
    totalCount: 0,
  },
])

const roomOptions = reactive({
  tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
  grid: { left: '3%', right: '4%', bottom: '3%', top: 30, containLabel: true },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: [],
    axisTick: { alignWithLabel: true },
    axisLabel: { interval: 0, fontSize: 11 },
  },
  yAxis: { type: 'value', name: 'Total Count', minInterval: 1 },
  series: [
    {
      name: 'Total Rooms',
      type: 'line',
      smooth: true,
      showSymbol: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2.5, color: '#409eff' },
      itemStyle: { color: '#409eff' },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(64,158,255,.35)' },
          { offset: 1, color: 'rgba(64,158,255,.03)' },
        ]),
      },
      data: [],
    },
  ],
  color: ['#409eff'],
})

const hasRoomData = computed(() => roomOptions.series[0].data.length > 0)

function getPreviousDay(date = new Date()) {
  return convertDate(moment(date).add(-1, 'days'))
}

function buildLabel(recordTime) {
  if (recordTime === null) return '-'
  const d = convertDate(recordTime)
  if (d === convertDate(new Date())) return 'Today'
  if (d === getPreviousDay()) return 'Yesterday'
  return convertDate(recordTime, 'MM/DD')
}

function buildRoomChart(list) {
  const valid = (list || []).filter(r => r.recordTime !== null)
  valid.sort((a, b) => new Date(a.recordTime) - new Date(b.recordTime))
  roomOptions.xAxis.data = valid.map(r => buildLabel(r.recordTime))
  roomOptions.series[0].data = valid.map(r => Number(r.totalCount) || 0)
}

async function getStatsRealTime() {
  const { data: statResult } = await getRealTimeStats()
  stats.value = statResult
}

async function getStatsRoom() {
  const { data: res } = await getRoomStats()
  room.value = res
  buildRoomChart(res)
}

onMounted(async () => {
  await getStatsRealTime()
  await getStatsRoom()
})

onBeforeUnmount(() => {
  clearInterval(timer)
})

onBeforeMount(() => {
  timer = setInterval(() => {
    getStatsRealTime()
    getStatsRoom()
  }, 30000)
})
</script>

<style scoped>
.home-dashboard {
  padding: 16px;
}
.dashboard-main {
  margin-top: 16px;
}
.card-title {
  font-size: 16px;
  font-weight: 600;
}
.room-chart {
  height: 360px;
  width: 100%;
}
.room-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--el-text-color-secondary, #909399);
}
.dashboard-head :deep(.el-card),
.dashboard-main :deep(.el-card) {
  height: 100%;
}
</style>
